import React from 'react';
import dayjs from 'dayjs';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import StatCard from '../components/StatCard';
import ChartBlock from '../components/ChartBlock';
import TimelineChart from '../components/TimelineChart';
import styles from '../components/stats.module.css';
import { SCHEDULE_SERIES, SCHEDULE_TOTAL } from './ScheduleSection';
import { HELP } from '../utils/help';
import { formatNumber } from '../utils/format';
import { goalHint } from '../utils/goal';
import { currentMonthForecast } from '../utils/forecast';

const HIGHLIGHT_ICONS = {
  money: <PaymentsOutlinedIcon />,
  receipt: <ReceiptLongOutlinedIcon />,
  noshow: <EventBusyOutlinedIcon />,
  patients: <PersonAddAltOutlinedIcon />,
  leads: <CampaignOutlinedIcon />,
  satisfaction: <SentimentSatisfiedAltOutlinedIcon />,
};

const TONE_ICONS = {
  good: <TrendingUpRoundedIcon />,
  bad: <ReportProblemOutlinedIcon />,
  neutral: <InfoOutlinedIcon />,
};

const TONE_CLASS = {
  good: styles.highlightGood,
  bad: styles.highlightBad,
  neutral: styles.highlightNeutral,
};

/** Frases do que mudou ou pede atenção no período (geradas no backend). */
export const Highlights = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className={styles.highlights}>
      {items.map((item, index) => (
        <div key={index} className={`${styles.highlight} ${TONE_CLASS[item.tone] || styles.highlightNeutral}`}>
          {item.tone === 'neutral' ? (HIGHLIGHT_ICONS[item.icon] || TONE_ICONS.neutral) : TONE_ICONS[item.tone]}
          <span>{item.text}</span>
        </div>
      ))}
    </div>
  );
};

/** KPIs da visão geral; os bloqueados pelo plano não aparecem aqui (a seção deles mostra o cadeado). */
export function overviewCards(kpis) {
  return [
    { key: 'received', label: 'Recebido', icon: <PaymentsOutlinedIcon />, type: 'money', accent: 'green' },
    { key: 'done', label: 'Atendimentos realizados', icon: <EventAvailableOutlinedIcon />, accent: 'blue', help: HELP.done },
    { key: 'seen', label: 'Pacientes atendidos', icon: <PeopleAltOutlinedIcon />, accent: 'purple', help: HELP.seen },
    { key: 'newPatients', label: 'Cadastros novos', icon: <PersonAddAltOutlinedIcon />, accent: 'teal' },
    { key: 'noshowRate', label: 'Taxa de faltas', icon: <EventBusyOutlinedIcon />, type: 'percent', inverse: true, accent: 'amber', help: HELP.noshowRate },
    { key: 'leadsConverted', label: 'Leads convertidos', icon: <CampaignOutlinedIcon />, accent: 'pink' },
    { key: 'nps', label: 'NPS', icon: <SentimentSatisfiedAltOutlinedIcon />, accent: 'green' },
  ].filter((card) => kpis[card.key]);
}

/**
 * Card "Previsto em <mês>": o total do mês atual já somado (realizados + o que
 * ainda está marcado até o fim do mês), com a conta e a meta embaixo.
 */
const ForecastCard = ({ forecast, goal }) => {
  const now = dayjs();
  const month = { start: now.startOf('month').format('YYYY-MM-DD'), end: now.endOf('month').format('YYYY-MM-DD') };
  return (
    <StatCard
      label={`Previsto em ${forecast.month}`}
      icon={<FlagOutlinedIcon />}
      accent="teal"
      kpi={{ value: forecast.expected }}
      help={HELP.monthForecast}
      hint={(
        <>
          <span>{`${formatNumber(forecast.done)} realizados + ${formatNumber(forecast.upcoming)} por vir`}</span>
          {goal && <span>{goalHint(forecast.expected, goal, month)}</span>}
        </>
      )}
    />
  );
};

const OverviewSection = ({ data, granularity, period, goal }) => {
  const forecast = currentMonthForecast(data.timeline, granularity, period);
  const cards = overviewCards(data.kpis);
  const doneIndex = cards.findIndex((card) => card.key === 'done');
  return (
  <>
    <Highlights items={data.highlights} />
    <div className={styles.grid}>
      {cards.map((card, index) => (
        <React.Fragment key={card.key}>
          <StatCard {...card} kpi={data.kpis[card.key]} />
          {forecast && index === doneIndex && <ForecastCard forecast={forecast} goal={goal} />}
        </React.Fragment>
      ))}
    </div>
    <ChartBlock title="Atendimentos no período">
      <TimelineChart
        data={data.timeline}
        granularity={granularity}
        series={SCHEDULE_SERIES}
        total={SCHEDULE_TOTAL}
        goal={goal}
        height={200}
      />
    </ChartBlock>
  </>
  );
};

export default OverviewSection;
